import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Plus, List, Table2, Calendar } from 'lucide-react';
import { useProjectStore } from '../store/projectStore';
import { useTaskStore } from '../store/taskStore';
import { KanbanBoard } from '../components/Kanban/KanbanBoard';
import { TaskDetailPanel } from '../components/Tasks/TaskDetailPanel';
import { TaskTableView } from '../components/Tasks/TaskTableView';
import { TaskCalendarView } from '../components/Tasks/TaskCalendarView';
import { TaskPeople } from '../components/Tasks/TaskPeople';
import { Modal } from '../components/common/Modal';
import { Button } from '../components/common/Button';
import { Badge, getStatusVariant, getPriorityVariant, statusLabels, priorityLabels } from '../components/common/Badge';
import { Task, User } from '../types';
import { usersApi } from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

type ViewMode = 'kanban' | 'list' | 'table' | 'calendar';

const PROJECT_VIEW_MODE_STORAGE_KEY = 'project-detail-view-mode';
const VIEW_MODES: ViewMode[] = ['kanban', 'list', 'table', 'calendar'];

function getInitialViewMode(): ViewMode {
  const storedViewMode = window.localStorage.getItem(PROJECT_VIEW_MODE_STORAGE_KEY);
  return VIEW_MODES.includes(storedViewMode as ViewMode) ? storedViewMode as ViewMode : 'kanban';
}

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { currentProject, fetchProject } = useProjectStore();
  const { tasks, loading, fetchTasks, createTask, updateTask, deleteTask, moveTask } = useTaskStore();
  const [viewMode, setViewMode] = useState<ViewMode>(getInitialViewMode);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [defaultStatus, setDefaultStatus] = useState<Task['status']>('todo');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [members, setMembers] = useState<User[]>([]);

  useEffect(() => {
    if (id) {
      fetchProject(id);
      fetchTasks(id);
      usersApi.getAll()
        .then(res => setMembers(res.data))
        .catch(() => {});
    }
  }, [id, fetchProject, fetchTasks]);

  const handleAddTask = (status: Task['status'] = 'todo') => {
    setDefaultStatus(status);
    setIsTaskModalOpen(true);
  };

  const handleCreateTask = async (data: Partial<Task>) => {
    try {
      await createTask({ ...data, project_id: id! });
      setIsTaskModalOpen(false);
      toast.success('Tarefa criada!');
    } catch {
      toast.error('Falha ao criar tarefa');
    }
  };

  const handleUpdateTask = async (data: Partial<Task>) => {
    if (!selectedTask) return;
    try {
      await updateTask(selectedTask.id, data);
      setSelectedTask(null);
      toast.success('Tarefa atualizada!');
    } catch {
      toast.error('Falha ao atualizar tarefa');
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Excluir esta tarefa?')) return;
    try {
      await deleteTask(taskId);
      setSelectedTask(null);
      toast.success('Tarefa excluída');
    } catch {
      toast.error('Falha ao excluir tarefa');
    }
  };

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    window.localStorage.setItem(PROJECT_VIEW_MODE_STORAGE_KEY, mode);
  };

  if (!currentProject) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/projects" className="text-gray-400 hover:text-gray-600 transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: currentProject.color }} />
              <h1 className="text-xl font-bold text-gray-900">{currentProject.name}</h1>
            </div>
            <div className="flex gap-2">
              <Badge variant={getStatusVariant(currentProject.status)}>
                {statusLabels[currentProject.status] || currentProject.status}
              </Badge>
              <Badge variant={getPriorityVariant(currentProject.priority)}>
                {priorityLabels[currentProject.priority] || currentProject.priority}
              </Badge>
            </div>
          </div>

        </div>

        {currentProject.description && (
          <p className="text-sm text-gray-500 mt-2 ml-12">{currentProject.description}</p>
        )}

        <div className="flex items-center gap-3 flex-wrap mt-4 ml-12">
          {/* View toggle */}
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => handleViewModeChange('kanban')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${viewMode === 'kanban' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
              title="Kanban"
            >
              Kanban
            </button>
            <button
              onClick={() => handleViewModeChange('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${viewMode === 'list' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
              title="Lista"
            >
              <List size={14} />
              Lista
            </button>
            <button
              onClick={() => handleViewModeChange('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${viewMode === 'table' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
              title="Tabela"
            >
              <Table2 size={14} />
              Tabela
            </button>
            <button
              onClick={() => handleViewModeChange('calendar')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${viewMode === 'calendar' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
              title="Calendário"
            >
              <Calendar size={14} />
              Calendário
            </button>
          </div>

          <Button icon={<Plus size={16} />} size="sm" onClick={() => handleAddTask()}>
            Adicionar Tarefa
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : viewMode === 'kanban' ? (
          <KanbanBoard
            tasks={tasks}
            onMoveTask={moveTask}
            onTaskClick={setSelectedTask}
            onAddTask={handleAddTask}
          />
        ) : viewMode === 'table' ? (
          <TaskTableView
            tasks={tasks}
            onTaskClick={setSelectedTask}
            onAddTask={handleAddTask}
            onStatusChange={async (taskId, status) => {
              try {
                await updateTask(taskId, { status });
                toast.success('Status atualizado!');
              } catch {
                toast.error('Falha ao atualizar status');
              }
            }}
          />
        ) : viewMode === 'calendar' ? (
          <TaskCalendarView
            tasks={tasks}
            onTaskClick={setSelectedTask}
            onAddTask={handleAddTask}
          />
        ) : (
          <TaskListView
            tasks={tasks}
            onTaskClick={setSelectedTask}
            onDeleteTask={handleDeleteTask}
          />
        )}
      </div>

      {/* Create Task Modal */}
      <Modal isOpen={isTaskModalOpen} onClose={() => setIsTaskModalOpen(false)} title="" size="wide">
        <TaskDetailPanel
          projectId={id!}
          projectName={currentProject.name}
          defaultStatus={defaultStatus}
          initialData={{ status: defaultStatus, project_name: currentProject.name }}
          members={members}
          onSubmit={handleCreateTask}
          onCancel={() => setIsTaskModalOpen(false)}
        />
      </Modal>

      {/* Task Detail Modal */}
      <Modal isOpen={!!selectedTask} onClose={() => setSelectedTask(null)} title="" size="wide">
        {selectedTask && (
          <TaskDetailPanel
            projectId={id!}
            projectName={currentProject.name}
            initialData={selectedTask}
            members={members}
            onSubmit={handleUpdateTask}
            onCancel={() => setSelectedTask(null)}
            onDelete={handleDeleteTask}
          />
        )}
      </Modal>
    </div>
  );
}

function TaskListView({ tasks, onTaskClick, onDeleteTask }: {
  tasks: Task[];
  onTaskClick: (task: Task) => void;
  onDeleteTask: (id: string) => void;
}) {
  const statusGroups: Record<Task['status'], string> = {
    todo: 'A Fazer',
    in_progress: 'Em Andamento',
    in_review: 'Em Revisão',
    done: 'Concluído',
  };

  return (
    <div className="space-y-6">
      {(Object.keys(statusGroups) as Task['status'][]).map(status => {
        const statusTasks = tasks.filter(t => t.status === status);
        if (statusTasks.length === 0) return null;

        return (
          <div key={status}>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              {statusGroups[status]} ({statusTasks.length})
            </h3>
            <div className="space-y-2">
              {statusTasks.map(task => (
                <div
                  key={task.id}
                  className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-4 hover:shadow-sm transition-shadow cursor-pointer"
                  onClick={() => onTaskClick(task)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
                    {task.description && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{task.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <Badge variant={getPriorityVariant(task.priority)}>{priorityLabels[task.priority] || task.priority}</Badge>
                    <TaskPeople task={task} showNames />
                    {task.due_date && (
                      <span className="text-xs text-gray-400">
                        {format(new Date(task.due_date), 'd MMM', { locale: ptBR })}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
